import { RestError } from '@azure/core-http';
import {
  CreateCarrierOrganisationDto,
  CreateCarrierOrganisationResponseDto,
  EditCarrierOrganisationDto,
  ServicingGrantDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { QueryKeys } from '../../core/queries/query-keys.constants';

/** The organisation, a servicing grant per network, and a registrations group, in one transaction. */
export function useMutationCreateCarrierOrganisation() {
  const api = useApi();

  return useMutation<CreateCarrierOrganisationResponseDto, RestError, { dto: CreateCarrierOrganisationDto }>(
    async ({ dto }) => {
      const result = await api.carrierOrganisationsController.create(dto);
      // The API doesn't throw an error on 4xx or 5xx, instead it returns an object with a body that
      // contains the error message. This is a workaround to throw an error in the mutation.
      const unknownResult = result as any;
      if ('body' in unknownResult && 'statusCode' in unknownResult.body && 'message' in unknownResult.body) {
        throw new RestError(unknownResult.body.message as string, undefined, unknownResult.body.statusCode as number);
      }
      return result;
    }
  );
}

/**
 * Additive and idempotent per permission: a permission the group already holds is skipped, so a
 * failed save is safe to retry in full.
 */
export function useMutationGrantServicing() {
  const api = useApi();

  return useMutation<void, RestError, { groupID: string; grant: ServicingGrantDto }>(
    async ({ groupID, grant }) => await api.carrierOrganisationsController.grantServicing(groupID, grant)
  );
}

/** Withdraws the right to work in a project. Couriers and delivery history are untouched. */
export function useMutationRevokeServicing() {
  const api = useApi();

  return useMutation<void, RestError, { groupID: string; grant: ServicingGrantDto }>(
    async ({ groupID, grant }) => await api.carrierOrganisationsController.revokeServicing(groupID, grant)
  );
}

/** Name and login method only. Registration policies live on the registration target unit. */
export function useMutationEditCarrierOrganisation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, RestError, { groupID: string; dto: EditCarrierOrganisationDto }>(
    async ({ groupID, dto }) => await api.carrierOrganisationsController.edit(groupID, dto),
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.CARRIERS]);
        queryClient.invalidateQueries([QueryKeys.GROUPS]);
      }
    }
  );
}

/** Where self-registrations land. Must be one of the organisation's operating units. */
export function useMutationSetRegistrationTarget() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, RestError, { groupID: string; registrationTargetGroupID: string }>(
    async ({ groupID, registrationTargetGroupID }) =>
      await api.carrierOrganisationsController.setRegistrationTarget(groupID, { registrationTargetGroupID }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.CARRIERS]);
      }
    }
  );
}
